// The mark of energy: a star with four points. It is the star of the logo,
// "Cut star" on the logo page. Garrett, 2026-10-13: "lets make that the
// universal energy symbol and use for chesscaster logo in a way". One energy
// is a coin with the star cut from it. Pure: these give path data.
// `--energy-star` in `tokens.css` has the same star for a CSS mask.

/**
 * How far each inner corner of the star is from the middle, as a share of the
 * reach of a point. The small form is wider, for a mark of 32px or less.
 */
const STAR_WAIST = { large: 8.5 / 33, small: 11 / 36 } as const;

export type StarForm = keyof typeof STAR_WAIST;

/** The star of a coin reaches this share of the radius of the coin. */
const COIN_STAR_RATIO = 0.8;

const round = (value: number): number => Math.round(value * 1000) / 1000;

/** The star with its points `radius` from the middle. */
export const energyStarPath = (
  radius: number,
  form: StarForm = 'small',
  cx = 0,
  cy = 0,
): string => {
  const waist = radius * STAR_WAIST[form];
  const corners: [number, number][] = [
    [0, -radius],
    [waist, -waist],
    [radius, 0],
    [waist, waist],
    [0, radius],
    [-waist, waist],
    [-radius, 0],
    [-waist, -waist],
  ];
  const points = corners.map(([x, y]) => `${round(cx + x)} ${round(cy + y)}`);
  return `M${points.join('L')}Z`;
};

/** The star as it lies in a coin of that radius. */
export const coinStarPath = (coinRadius: number, cx = 0, cy = 0): string =>
  energyStarPath(coinRadius * COIN_STAR_RATIO, 'small', cx, cy);

/**
 * The tile of the logo with the star cut from it, and the box it fills. The
 * large form is for a mark over 32px.
 */
export const logoTile = (form: StarForm): { path: string; viewBox: string } => {
  const inset = form === 'large' ? 12 : 8;
  const side = 100 - inset * 2;
  const reach = form === 'large' ? 33 : 36;
  return {
    path: `M${inset} ${inset}h${side}v${side}h${-side}Z${energyStarPath(reach, form, 50, 50)}`,
    viewBox: `${inset} ${inset} ${side} ${side}`,
  };
};
